import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiHttpError } from '~/lib/api/client'
import type {
  CreatePersonTimeOffPayload,
  TimeOffPayload,
} from '~/lib/api/types'
import styles from './time-off-shortcuts.module.scss'

type Props = {
  slackId: string
  displayName: string
  todayIso: string
  onCreated: () => void
}

const KIND_OPTIONS: {
  value: CreatePersonTimeOffPayload['kind']
  label: string
}[] = [
  { value: 'vacation', label: 'vacation' },
  { value: 'sick', label: 'sick' },
  { value: 'other', label: 'other' },
]

export function TimeOffShortcuts({
  slackId,
  displayName,
  todayIso,
  onCreated,
}: Props) {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createMutation = useMutation<
    TimeOffPayload,
    ApiHttpError,
    CreatePersonTimeOffPayload
  >({
    mutationFn: payload => api.createPersonTimeOff(slackId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person'] })
      qc.invalidateQueries({ queryKey: ['snapshot'] })
      setShowForm(false)
      onCreated()
    },
    onError: err => setError(messageForError(err)),
  })

  const handleSickToday = () => {
    if (!confirmSick(displayName, todayIso)) return
    setError(null)
    createMutation.mutate({
      kind: 'sick',
      start_date: todayIso,
      end_date: todayIso,
    })
  }

  return (
    <div className={styles.toolbar}>
      <button
        type="button"
        className={styles.btn}
        onClick={handleSickToday}
        disabled={createMutation.isPending}
      >
        Mark sick day today
      </button>

      <button
        type="button"
        className={styles.btn}
        onClick={() => {
          setError(null)
          setShowForm(s => !s)
        }}
        aria-expanded={showForm}
        aria-controls="time-off-add-form"
      >
        {showForm ? 'Cancel' : 'Add OOO…'}
      </button>

      {showForm ? (
        <AddForm
          submitting={createMutation.isPending}
          todayIso={todayIso}
          onSubmit={payload => {
            setError(null)
            createMutation.mutate(payload)
          }}
        />
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

function AddForm({
  submitting,
  todayIso,
  onSubmit,
}: {
  submitting: boolean
  todayIso: string
  onSubmit: (payload: CreatePersonTimeOffPayload) => void
}) {
  const [kind, setKind] =
    useState<CreatePersonTimeOffPayload['kind']>('vacation')
  const [start, setStart] = useState(todayIso)
  const [end, setEnd] = useState(todayIso)
  const [note, setNote] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      kind,
      start_date: start,
      end_date: end,
      note: note || null,
    })
  }

  return (
    <form
      id="time-off-add-form"
      className={styles.form}
      onSubmit={handleSubmit}
    >
      <div className={styles.field}>
        <label htmlFor="ooo-kind">Kind</label>
        <select
          id="ooo-kind"
          className={styles.input}
          value={kind}
          onChange={e =>
            setKind(e.target.value as CreatePersonTimeOffPayload['kind'])
          }
        >
          {KIND_OPTIONS.map(k => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.row2}>
        <div className={styles.field}>
          <label htmlFor="ooo-start">Start</label>
          <input
            id="ooo-start"
            type="date"
            className={styles.input}
            value={start}
            onChange={e => setStart(e.target.value)}
            required
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="ooo-end">End</label>
          <input
            id="ooo-end"
            type="date"
            className={styles.input}
            value={end}
            onChange={e => setEnd(e.target.value)}
            required
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="ooo-note">Note (optional)</label>
        <input
          id="ooo-note"
          type="text"
          className={styles.input}
          value={note}
          onChange={e => setNote(e.target.value)}
          maxLength={200}
        />
      </div>

      <div className={styles.formActions}>
        <button
          type="submit"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

function confirmSick(name: string, dateIso: string): boolean {
  if (typeof window === 'undefined') return true
  return window.confirm(`Mark ${name} as sick on ${dateIso}?`)
}

function messageForError(err: ApiHttpError | Error): string {
  if (err instanceof ApiHttpError) {
    const body = err.body as { error?: { code?: string; message?: string } }
    if (body?.error?.message) return body.error.message
  }
  return 'Could not save time off. Try again.'
}
